/**
 * EffectsSpecial.js
 * Section 3 of ResultEffects: Special skills shown individually.
 * (Evolution, Gold, Inherited, Unique, Scenario Gold)
 * No Total column — user analyzes at their own discretion.
 */
import React, { useState } from 'react';
import {
    getStyle, getEffectsFromScenario, collectEffectNames,
    stickyLeft, ScrollTable, EffectCell, SectionHeader, EffectNameCell,
} from './effectsHelpers';

const EffectsSpecial = ({ specialSkills }) => {
    const [open, setOpen] = useState(true);
    if (specialSkills.length === 0) return null;

    const effectNames = collectEffectNames(specialSkills);
    if (effectNames.length === 0) return null;

    return (
        <div className="space-y-2">
            <SectionHeader
                open={open}
                onToggle={() => setOpen(o => !o)}
                dotColor="bg-amber-400"
                title="Special Skills"
                subtitle={`${specialSkills.length} skill${specialSkills.length !== 1 ? 's' : ''} · no total`}
            />
            {open && (
                <div className="rounded-xl border border-slate-700">
                    <ScrollTable minWidth={`${160 + specialSkills.length * 100}px`}>
                        <thead>
                            <tr className="bg-slate-900 border-b border-slate-700">
                                <th className={`${stickyLeft} bg-slate-900 px-4 py-2.5 text-[9px]
                                    font-bold uppercase tracking-widest text-slate-500 w-40
                                    border-r border-slate-700/60`}>
                                    Effect
                                </th>
                                {specialSkills.map((skill, idx) => {
                                    const st = getStyle(skill.color);
                                    return (
                                        <th key={skill.Id_skill} className="px-3 py-2.5 text-center min-w-[90px]">
                                            <div className="flex flex-col items-center gap-0.5">
                                                <span className="inline-flex items-center justify-center
                                                    w-5 h-5 rounded-full text-[9px] font-bold
                                                    bg-violet-900/60 text-violet-300 border border-violet-700/50">
                                                    #{idx + 1}
                                                </span>
                                                {skill.color && (
                                                    <span className={`text-[8px] px-1 py-0.5 rounded border ${st.pill}`}>
                                                        {skill.color.replace(' Skills', '').replace(' Skill', '')}
                                                    </span>
                                                )}
                                            </div>
                                        </th>
                                    );
                                })}
                            </tr>
                        </thead>
                        <tbody>
                            {effectNames.map((name, rowIdx) => (
                                <tr key={name}
                                    className={`border-b border-slate-700/40 last:border-0
                                        ${rowIdx % 2 === 0 ? 'bg-slate-800/40' : 'bg-slate-800/10'}`}>
                                    <EffectNameCell name={name} rowIdx={rowIdx} />
                                    {specialSkills.map(skill => (
                                        <td key={skill.Id_skill} className="px-3 py-2.5 text-center">
                                            <EffectCell entries={getEffectsFromScenario(skill)[name]} />
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </ScrollTable>

                    {/* Legend */}
                    <div className="px-4 py-2.5 bg-slate-900/30 border-t border-slate-700
                        flex flex-wrap gap-x-4 gap-y-1">
                        {specialSkills.map((skill, idx) => (
                            <span key={skill.Id_skill} className="flex items-center gap-1.5">
                                <span className="inline-flex items-center justify-center w-4 h-4 rounded-full
                                    text-[8px] font-bold bg-violet-900/60 text-violet-300 border border-violet-700/50">
                                    {idx + 1}
                                </span>
                                <span className="text-[10px] text-slate-400 truncate max-w-[160px]"
                                    title={skill.skillName}>{skill.skillName}</span>
                            </span>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default EffectsSpecial;
